import {
  Box,
  CircularProgress,
  ImageList,
  ImageListItem,
  type ImageListItemProps,
  type ImageListProps,
  Typography,
} from '@mui/material';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { capitalizeString } from '../../utils/capitalizeString/capitalizeString';
import { EvidenceImage } from '../EvidenceImage/EvidenceImage';
import type { ResultIconProps } from '../ResultIcon/ResultIcon';

export type PhotoEvidenceItem = Pick<ImageListItemProps, 'cols' | 'rows'> & {
  src: string;
  thumbnailSrc?: string;
  placeholderSrc?: string;
  isLoading?: boolean;
  uploadProgress?: number;
  variant?: ResultIconProps['variant'];
  imageRejectionReasons?: string[];
};

type Props = {
  openOnClick?: boolean;
  placeholderSrc?: string;
  evidencePhotos: PhotoEvidenceItem[];
  direction?: 'row' | 'column';
  slotProps?: {
    imageList?: Omit<ImageListProps, 'children'>;
    resultIcon?: Omit<ResultIconProps, 'variant'>;
  };
  handleClick?: (photo: PhotoEvidenceItem) => void;
  isLoading?: boolean;
  hideMessage?: boolean;
};

export const PhotoEvidenceList = (props: Props) => {
  const { evidencePhotos, slotProps, openOnClick } = props;
  const { isMobile } = useBreakpoint();

  if (props.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (!evidencePhotos.length && !props.hideMessage) {
    return <Typography>No photos available</Typography>;
  }

  return (
    <ImageList
      gap={isMobile ? 10 : 20}
      cols={isMobile ? 2 : 4}
      {...slotProps?.imageList}
      sx={{
        gridAutoFlow: props.direction === 'row' ? 'column' : 'row',
        my: 0,
        ...(props.direction === 'row' && {
          gridTemplateColumns: 'auto !important',
          gridTemplateRows: `repeat(${slotProps?.imageList?.cols ?? 2}, 1fr)`,
        }),
      }}
    >
      {evidencePhotos.map((photo) => {
        const {
          thumbnailSrc,
          placeholderSrc,
          src,
          isLoading: isPhotoLoading,
          uploadProgress,
          variant,
          cols,
          rows,
          imageRejectionReasons,
        } = photo;
        return (
          <ImageListItem
            key={src}
            sx={{
              cursor: 'pointer',
            }}
            cols={cols}
            rows={rows}
          >
            <EvidenceImage
              src={thumbnailSrc || src}
              onClick={() => openOnClick && props.handleClick?.(photo)}
              loading="lazy"
              placeholderSrc={placeholderSrc ?? props.placeholderSrc}
              isLoading={isPhotoLoading}
              uploadProgress={uploadProgress}
              slotProps={{
                resultIcon: {
                  variant: variant,
                  ...slotProps?.resultIcon,
                },
              }}
            />
            {imageRejectionReasons?.map((reason) => (
              <Typography key={reason} variant="caption" color="error">
                {capitalizeString(reason)}
              </Typography>
            ))}
          </ImageListItem>
        );
      })}
    </ImageList>
  );
};
